// Beat: add new, update, delete.
// Event: add new, change duration, delete, mark artifact, mark/unmark reviewed.
// Capture event: capture, edit technician comment, remove strips.
// HR Summary -> Min/Avg/Max: update giá trị khác.
// Book mark: add new, delete.
// Strips management:
// Device events: Valid/Invalid, Include in report, Include Strip, update technician comment, captured lại strips.
// Auto events: Delete, Include in report, Include Strip, update technician comment, captured lại strips.
// ECG disclosure: set as ECG disclosure.
// PDF report: Update Findings.

import _ from 'lodash';
import { calculateHrInfoByDate } from '@/entities/heart-rate';
import { TAB_KEY } from '@/shared/config';
import { getDataState } from '@/shared/lib';
import { convertToMomentJs } from '@/shared/lib/aiUtils';

// Beat, Event, Capture event, HR Summary, Book mark
const CHANGE_SAVED_BEATS_KEYS = [
  'beatChangesState-BEAT_HR',
  'eventNewsState-BEAT_HR',
  'eventChangesState-BEAT_HR',
  'eventOthersState-BEAT_HR',
  'groupEventChangesState-BEAT_HR',
  'updatedDailySummariesState',
  'ecgBookmarksState', // check if no id is new bookmark
  'ecgBookmarkDeleteIdsState',
  'bulkBeatsChangesState-BEAT_HR',
  'bulkReviewedBeatsState',
  'reviewedBeatsChangesState',
  'swapBeatChangesState-BEAT_HR',
  'bulkSwapBeatsState-BEAT_HR',
  'templateChangesState-BEAT_HR',
];

// Beat, Event, Capture event, Book mark
const CHANGE_SAVED_ECG_EVENTS_KEYS = [
  'beatChangesState-RHYTHM_EVENT',
  'eventNewsState-RHYTHM_EVENT',
  'eventChangesState-RHYTHM_EVENT',
  'eventOthersState-RHYTHM_EVENT',
  'groupEventChangesState-RHYTHM_EVENT',
  'ecgBookmarkDeleteIdsState',
  'ecgBookmarksState', // check if no id is new bookmark
];

const CHANGE_SAVED_STRIPS_KEYS = [
  'changeStripDataState-STRIP_MANAGEMENT',
  'changeStripDataState-DEVICE_MANAGEMENT',
];

const CHANGE_SAVED_HR_SUMMARY_KEYS = [
  'beatChangesState-HR_SUMMARY',
  'eventNewsState-HR_SUMMARY',
  'eventChangesState-HR_SUMMARY',
  'eventOthersState-HR_SUMMARY',
  'groupEventChangesState-HR_SUMMARY',
  'updatedDailySummariesState',
  'ecgBookmarksState', // check if no id is new bookmark
  'ecgBookmarkDeleteIdsState',
];

// Keyed theo keyNumber (TAB_KEY, giữ nguyên chuỗi số đã persist trong IndexedDB).
const getCheckingChangeKeys: Record<string, string[]> = {
  [TAB_KEY.BEATS]: CHANGE_SAVED_BEATS_KEYS, // '1' Beats
  [TAB_KEY.ECG_EVENTS]: CHANGE_SAVED_ECG_EVENTS_KEYS, // '2' ECG Events
  [TAB_KEY.HR_SUMMARY]: CHANGE_SAVED_HR_SUMMARY_KEYS, // '6' HR Summary
  [TAB_KEY.STRIPS_MANAGEMENT]: CHANGE_SAVED_STRIPS_KEYS, // '3' Strips Management
};

/**
 * Reports whether one persisted report-state key still holds unsaved edits. Reads the saved value from IndexedDB and treats a non-empty value as changed, except for ecgBookmarksState where only a bookmark lacking an id or marked isNew counts.
 *
 * @param key - Persisted report-state key to inspect, such as ecgBookmarksState.
 * @returns A promise resolving to true when that key holds unsaved changes.
 */
const checkChange = async (key: string) => {
  let isChanged = false;
  const saved = await getDataState(key);
  if (!saved?.value) return false;
  if (key === 'ecgBookmarksState') {
    const isNewBookMark = _.find(saved.value, (bookmark) => !bookmark.id || bookmark.isNew);
    if (isNewBookMark) {
      isChanged = true;
    }
  } else if (!_.isEmpty(saved?.value)) {
    isChanged = true;
  }
  return isChanged;
};

// currentEcgDisclosure shape needs backend contract knowledge; left as any.
/**
 * Reports whether the ECG disclosure selection differs from the copy persisted in IndexedDB. Recomputes the heart-rate info for the selection's start and stop moments in the study timezone, merges it into a clone of the current disclosure, and deep-compares that against the saved ecgDisclosureChangeState value.
 *
 * @param currentEcgDisclosure - Current disclosure selection carrying ecgDisclosure, timezoneOffset and ecgDataMap.
 * @returns A promise resolving to true when the current disclosure selection differs from the saved one.
 */
const checkChangeEcgDisclosure = async (currentEcgDisclosure: any) => {
  const { ecgDisclosure, timezoneOffset, ecgDataMap } = currentEcgDisclosure || {};
  if (_.isEmpty(currentEcgDisclosure) || _.isEmpty(ecgDisclosure)) return false;
  const saved = await getDataState('ecgDisclosureChangeState');
  if (!saved?.value) return false;
  const clonedSelectedEcgDisclosure = _.cloneDeep(ecgDisclosure);
  const hrInfo = calculateHrInfoByDate({
    ecgDataMap,
    start: convertToMomentJs(clonedSelectedEcgDisclosure?.start, timezoneOffset),
    stop: convertToMomentJs(clonedSelectedEcgDisclosure?.stop, timezoneOffset),
  });
  _.assign(clonedSelectedEcgDisclosure, hrInfo);
  return !_.isEqual(clonedSelectedEcgDisclosure, saved?.value);
};

/**
 * Decides whether the active report tab has unsaved edits waiting in IndexedDB. Checks the persisted findings flag, runs checkChange over the keys registered for that tab, additionally compares the ECG disclosure selection on tab 3, and swallows any read failure by reporting no change.
 *
 * @param activeTab - Identifier of the report tab whose registered state keys are checked.
 * @param currentEcgDisclosure - Current disclosure selection, compared only when the ECG disclosure tab is active.
 * @returns A promise resolving to true when the tab has unsaved changes.
 */
const checkHasChangedSavedIndexDB = async ({
  activeTab,
  currentEcgDisclosure,
}: {
  activeTab: string | number;
  currentEcgDisclosure: any;
}) => {
  try {
    const findingSaved = await getDataState('findingsChangeState');
    const isChangedFinding = !_.isNil(findingSaved?.value) && findingSaved?.value;
    const keys = getCheckingChangeKeys[activeTab as unknown as keyof typeof getCheckingChangeKeys];
    const promises: Promise<boolean>[] = [];
    _.forEach(keys, async (key) => {
      promises.push(checkChange(key));
    });
    const result = await Promise.all(promises);
    let isChanged = _.find(result, (changed) => !!changed);
    if (activeTab === TAB_KEY.STRIPS_MANAGEMENT) {
      // ECG disclosure
      const isEcgDisclosureChanged = await checkChangeEcgDisclosure(currentEcgDisclosure);
      isChanged = isChanged || isEcgDisclosureChanged;
    }
    return isChanged || isChangedFinding;
  } catch (error) {
    console.error('checkHasChangedSavedIndexDB error:', error);
    return false;
  }
};

// eslint-disable-next-line import/prefer-default-export
export { checkHasChangedSavedIndexDB };
